import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useCallback, useContext, useMemo, useState } from "react";
import MocPDFTemplate from "../../../components/PDF/Reporting/moc/MocPDFTemplate";
import ReportingFormDrawerV2 from "../../../components/Reporting/ReportingFormDrawerV2";
import { ReportStreamColumn } from "../../../components/Reporting/stream/ReportStreamList";
import ReportStreamPage from "../../../components/Reporting/stream/ReportStreamPage";
import {
	ReportStreamItemsSummary,
	ReportStreamMeta,
	ReportStreamNumberCell,
	ReportStreamRowMenu,
	ReportStreamStatusChip,
	ReportStreamTitle,
} from "../../../components/Reporting/stream/ReportStreamParts";
import { HomeContext } from "../../../containers/Home";
import {
	ReportStreamConfig,
	useReportStream,
} from "../../../hooks/useReportStream";
import {
	ReportItem,
	ReportStreamAssets,
} from "../../../hooks/useReportStreamFilter";
import {
	useOperatorRecords,
	useReportPhases,
	useReportStreamNames,
	useTenantName,
} from "../../../hooks/useReportStreamLookups";
import { getMocCategoriesList } from "../../../services/api-server/moc_categories";
import { fetchReportingHubReports } from "../../../services/reporting-hub/fetchReports";
import { useTenantStore } from "../../../stores/tenantStore";
import { formatDateDdMmmYyyy } from "../../../utils/date_formatter";
import { createMocLookupConfig } from "../../../utils/reporting/lookupConfigs/moc";
import {
	getMocCategoryId,
	getMocCategoryLabel,
	getMocMeta,
	getMocSearchTexts,
	StreamMoc,
} from "../../../utils/reporting/mocStream";
import { REPORT_ITEM_KINDS } from "../../../utils/reporting/reportItemKinds";
import {
	collectAuthorEmails,
	getDateValue,
	getItemsSummary,
	getReportAuthorName,
	getReportNumberValue,
	StreamPlaces,
} from "../../../utils/reporting/reportStream";
import {
	DEFAULT_REPORT_STATUSES,
	ReportStatusKey,
} from "../../../utils/reporting/reportStatusOptions";
import {
	collectReportExportEmails,
	fetchExportNames,
	getReportExportData,
	lookUpReportForExport,
} from "../../../utils/reporting/reportStreamExport";
import { handleExportReport } from "../../../utils/reports";

type MocRow = ReportItem &
	StreamMoc & {
		submitted_on?: string;
		is_rejected?: boolean;
		follow_up_actions?: unknown;
	};

// MoCs never reach Approved - Open, so they have four figures.
const MOC_STATUSES: ReportStatusKey[] = [
	"in_review",
	"in_progress",
	"final_review",
	"closed",
];

const NO_MOCS: MocRow[] = [];

const getMocPath = (moc: MocRow) => `/reporting/moc-stream/${moc._id}`;

const fetchCategories = (operator: string, ids: string[]) =>
	getMocCategoriesList(operator, { _id: { $in: ids } });

// The MoC's PDF, as its page exports it: the page's lookups for this MoC
// alone and the names of everyone it names.
async function exportMoc(moc: MocRow) {
	const operators = moc.operator ? [moc.operator] : [];
	const [lookedUp, emailToName] = await Promise.all([
		lookUpReportForExport(moc, createMocLookupConfig(operators)),
		fetchExportNames(collectReportExportEmails(moc, "follow_up_actions")),
	]);
	await handleExportReport(
		getReportExportData(lookedUp, emailToName, "follow_up_actions"),
		"moc",
		MocPDFTemplate
	);
}

function MocStream() {
	const { currentTenantKey }: any = useContext(HomeContext);
	const isMasterTenant = useTenantStore((state) => state.isMasterTenant);
	const getTenantName = useTenantName();
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);

	// The Reporting Hub fills and reads the same entry, and a saved report
	// is written into it (ReportingFormDrawerV2), so the rows stay as loaded.
	const { data, isPending, isError, isFetching, refetch } = useQuery({
		queryKey: ["reporting-hub-data", "moc"],
		queryFn: () =>
			fetchReportingHubReports("moc", currentTenantKey, isMasterTenant),
		enabled: !!currentTenantKey,
		staleTime: 5 * 60 * 1000,
	});
	const mocs: MocRow[] | undefined = Array.isArray(data) ? data : undefined;
	const rows = mocs ?? NO_MOCS;

	// Only the listed authors are looked up by name, and only the listed
	// MoCs' phases and categories.
	const authorEmails = useMemo(() => collectAuthorEmails(rows), [rows]);
	const authorNames = useReportStreamNames(
		["moc-stream-authors"],
		authorEmails
	);
	const phaseMap = useReportPhases(rows, currentTenantKey);
	const categoryMap = useOperatorRecords(
		rows,
		"category",
		"report-stream-moc-categories",
		fetchCategories,
		currentTenantKey
	);

	// The category filter names its options once the categories load. Its
	// values are ids: the address splits values on commas, which names can
	// hold.
	const config = useMemo<ReportStreamConfig<MocRow>>(
		() => ({
			statuses: MOC_STATUSES,
			defaultStatuses: MOC_STATUSES.filter((key) =>
				DEFAULT_REPORT_STATUSES.includes(key)
			),
			hasInternalExternal: false,
			filters: [
				{
					key: "category",
					label: "MoC category",
					getValue: getMocCategoryId,
					getOptionLabel: (id) => getMocCategoryLabel(id, categoryMap),
				},
			],
			sortValues: {
				number: (moc) => getReportNumberValue(moc.report_number),
				date: (moc) => getDateValue(moc.submitted_on),
			},
		}),
		[categoryMap]
	);

	const getSearchTexts = useCallback(
		(moc: MocRow, assets: ReportStreamAssets) =>
			getMocSearchTexts(
				moc,
				{ ...assets, phaseMap },
				categoryMap,
				authorNames,
				getTenantName
			),
		[phaseMap, categoryMap, authorNames, getTenantName]
	);

	const view = useReportStream(
		mocs,
		config,
		getSearchTexts,
		currentTenantKey,
		isMasterTenant
	);
	const places = useMemo<StreamPlaces>(
		() => ({ ...view.assets, phaseMap }),
		[view.assets, phaseMap]
	);

	const today = dayjs();
	const columns: ReportStreamColumn<MocRow>[] = [
		{
			key: "number",
			header: "Report no.",
			width: "140px",
			narrow: "number",
			sortKey: "number",
			render: (moc) => (
				<ReportStreamNumberCell
					to={getMocPath(moc)}
					reportNumber={moc.report_number}
					operator={view.hasSeveralOperators ? moc.operator : undefined}
				/>
			),
		},
		{
			key: "moc",
			header: "MoC",
			width: "minmax(180px, 1fr)",
			narrow: "main",
			render: (moc) => {
				const { tag, parts } = getMocMeta(moc, places, categoryMap);
				return (
					<>
						<ReportStreamTitle title={moc.title} />
						<ReportStreamMeta tag={tag} parts={parts} />
					</>
				);
			},
		},
		{
			key: "date",
			header: "Date originated",
			width: "120px",
			narrow: "detail",
			sortKey: "date",
			render: (moc) => formatDateDdMmmYyyy(moc.submitted_on),
		},
		{
			key: "author",
			header: "Author",
			width: "140px",
			narrow: "detail",
			render: (moc) => (
				<span className="report-stream__ellipsis">
					{getReportAuthorName(moc, authorNames)}
				</span>
			),
		},
		{
			key: "actions",
			header: "Actions",
			width: "148px",
			narrow: "full",
			render: (moc) => (
				<ReportStreamItemsSummary
					kind={REPORT_ITEM_KINDS.follow_up_actions}
					summary={getItemsSummary(moc.follow_up_actions, moc, today)}
				/>
			),
		},
		{
			key: "status",
			header: "Status",
			width: "132px",
			narrow: "status",
			render: (moc) => (
				<ReportStreamStatusChip
					status={moc.status}
					openStatus={moc.open_status}
				/>
			),
		},
	];

	return (
		<div className="generic-container overflow-auto">
			<ReportStreamPage
				title="Management of Change"
				subtitle={
					isMasterTenant ? "MoCs from every tenant." : "Your tenant's MoCs."
				}
				noun="MoC"
				plural="MoCs"
				newLabel="New MoC"
				onNew={() => setIsDrawerOpen(true)}
				emptyText={
					isMasterTenant
						? "MoCs from every tenant will show here."
						: "MoCs your tenant submits will show here."
				}
				searchPlaceholder="Search number, title, description, category, asset"
				view={view}
				isLoading={isPending}
				loadFailed={isError && !mocs}
				isRetrying={isFetching}
				onRetry={() => void refetch()}
				columns={columns}
				getRowKey={(moc) => moc._id}
				renderRowMenu={(moc) => (
					<ReportStreamRowMenu
						reportNumber={moc.report_number}
						path={getMocPath(moc)}
						tenantKey={currentTenantKey}
						onExport={() => exportMoc(moc)}
					/>
				)}
			/>
			<ReportingFormDrawerV2
				isOpen={isDrawerOpen}
				reportType="moc"
				onClose={() => setIsDrawerOpen(false)}
			/>
		</div>
	);
}

export default MocStream;
